import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Newspaper } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { Card } from '@shared/ui/containers/Card';
import { WidgetHeader } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';
import { fetchLatestNewsIssues, fetchIssueDownloadUrl, type NewsIssue } from '@features/news-stand/api';
import { useModuleStore } from '@shared/stores/moduleStore';
import { useNewsReadStore } from '@shared/stores/newsReadStore';
import { formatRelative } from '@shared/lib/format';
import { toast } from '@shared/ui/feedback/Toast';

/**
 * Home widget: the latest 3 published news articles, with an unread dot that
 * clears (persisted locally) once the member opens an article.
 */
export function NewsWidget({ config }: WidgetProps): JSX.Element | null {
  const navigate = useNavigate();
  const newsEnabled = useModuleStore((s) => s.isModuleEnabled('newsstand'));
  const readIds = useNewsReadStore((s) => s.readIds);
  const markRead = useNewsReadStore((s) => s.markRead);
  const [openingId, setOpeningId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['news-latest', 3],
    queryFn: () => fetchLatestNewsIssues(3),
    enabled: newsEnabled,
    staleTime: 5 * 60_000,
  });

  if (!newsEnabled) return null;
  const issues = query.data ?? [];
  if (!query.isLoading && issues.length === 0) return null;

  async function open(issue: NewsIssue): Promise<void> {
    if (openingId) return;
    markRead(issue.article_id);
    setOpeningId(issue.article_id);
    try {
      const url = await fetchIssueDownloadUrl(issue.newsstand_id, issue.article_id);
      const params = new URLSearchParams({
        title: issue.name,
        ...(issue.file_name ? { file: issue.file_name } : {}),
      });
      navigate(
        `/app/news-stands/${issue.newsstand_id}/articles/${issue.article_id}/view?${params.toString()}`,
        { state: { pdfUrl: url, title: issue.name, fileName: issue.file_name } },
      );
    } catch (e) {
      toast.danger("Couldn't open article", e instanceof Error ? e.message : 'Please try again.');
    } finally {
      setOpeningId(null);
    }
  }

  return (
    <section>
      <WidgetHeader
        title={widgetTitle(config)}
        actionLabel="View all"
        onAction={() => navigate('/app/news-stands')}
      />
      <Card noGrain className="divide-y divide-hairline rounded-2xl p-0">
        {query.isLoading ? (
          <div className="space-y-3 p-4">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ) : (
          issues.map((issue) => {
            const unread = !readIds.includes(issue.article_id);
            return (
              <button
                key={issue.article_id}
                type="button"
                onClick={() => void open(issue)}
                className="flex w-full items-center gap-3 p-3.5 text-left active:bg-sunken"
              >
                {issue.cover_url ? (
                  <img
                    src={issue.cover_url}
                    alt=""
                    className="h-11 w-11 shrink-0 rounded-lg object-cover hairline"
                  />
                ) : (
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sunken text-ink-secondary">
                    <Newspaper className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className={'truncate text-body-sm ' + (unread ? 'font-semibold text-ink' : 'font-medium text-ink-secondary')}>
                      {issue.name}
                    </span>
                    {unread ? (
                      <span className="h-2 w-2 shrink-0 rounded-pill bg-primary-600" aria-label="Unread" />
                    ) : null}
                  </span>
                  {issue.published_at ? (
                    <span className="mt-0.5 block text-caption text-ink-muted">
                      {formatRelative(issue.published_at)}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })
        )}
      </Card>
    </section>
  );
}
